<script>
import { mapGetters } from 'vuex';
import MentionBox from '../mentions/MentionBox.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { ATTACHMENT_ICONS } from 'shared/constants/messages';

export default {
  components: { MentionBox, Avatar },
  props: {
    searchKey: {
      type: String,
      default: '',
    },
  },
  emits: ['replace'],
  computed: {
    ...mapGetters({
      cannedMessages: 'getCannedResponses',
    }),
    items() {
      if (!Array.isArray(this.cannedMessages)) {
        return [];
      }

      return this.cannedMessages
        .map(cannedMessage => {
          if (!cannedMessage || typeof cannedMessage !== 'object') {
            return null;
          }

          const attachments = Array.isArray(cannedMessage.attachments)
            ? cannedMessage.attachments
            : [];
          return {
            label: cannedMessage.short_code || '',
            key:
              cannedMessage.short_code ||
              cannedMessage.id ||
              Math.random().toString(),
            description: cannedMessage.content || '',
            attachments,
            attachmentsByType: this.groupAttachmentsByType(attachments),
          };
        })
        .filter(Boolean); // Remove any null items
    },
  },
  watch: {
    searchKey() {
      this.fetchCannedResponses();
    },
  },
  mounted() {
    this.fetchCannedResponses();
  },
  methods: {
    fetchCannedResponses() {
      this.$store.dispatch('getCannedResponse', { searchKey: this.searchKey });
    },
    handleMentionClick(item = {}) {
      this.$emit('replace', {
        content: item.description,
        attachments: item.attachments,
      });
    },
    groupAttachmentsByType(attachments) {
      if (!Array.isArray(attachments) || attachments.length === 0) {
        return {};
      }

      const grouped = {};
      attachments.forEach(attachment => {
        if (!attachment || typeof attachment !== 'object') {
          return; // Skip invalid attachments
        }

        const fileType = attachment.file_type || 'file';
        if (!grouped[fileType]) {
          grouped[fileType] = 0;
        }
        grouped[fileType] += 1;
      });
      return grouped;
    },
    getAttachmentIcon(fileType) {
      if (!fileType || typeof fileType !== 'string') {
        return ATTACHMENT_ICONS.fallback;
      }
      return ATTACHMENT_ICONS[fileType] || ATTACHMENT_ICONS.fallback;
    },
  },
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <MentionBox
    v-if="items.length"
    :items="items"
    @mention-select="handleMentionClick"
  >
    <template #default="{ item }">
      <div class="flex items-center gap-2">
        <Avatar :name="item.label" :size="32">
          <template #badge>
            <div
              v-if="item.attachments && item.attachments.length"
              class="absolute -right-1 -bottom-1 flex items-center justify-center bg-n-solid-1 rounded-full border border-n-strong"
            >
              <fluent-icon class="size-3 text-n-slate-11" icon="attach" />
            </div>
          </template>
        </Avatar>
        <div class="flex flex-col flex-1 min-w-0">
          <p
            class="max-w-full min-w-0 mb-0 overflow-hidden text-sm font-medium text-n-slate-11 text-ellipsis whitespace-nowrap"
          >
            {{ item.description }}
          </p>
          <p
            class="max-w-full min-w-0 mb-0 overflow-hidden text-xs text-n-slate-11 text-ellipsis whitespace-nowrap"
          >
            {{ `/${item.label}` }}
          </p>
          <div
            v-if="item.attachments && item.attachments.length"
            class="flex items-center gap-2 mt-1"
          >
            <div
              v-for="(count, fileType) in item.attachmentsByType"
              :key="fileType"
              class="flex items-center gap-1 text-xs text-n-slate-11"
            >
              <fluent-icon class="size-3" :icon="getAttachmentIcon(fileType)" />
              <span>{{ count }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </MentionBox>
</template>
